import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, uploadApi } from "../../lib/api";

export default function AdminJournalArticleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultIssueId = new URLSearchParams(location.search).get("issue_id") || "";
  const [issues, setIssues] = useState([]);
  const [form, setForm] = useState({
    issue_id: defaultIssueId,
    title: "",
    authors: "",
    abstract: "",
    keywords: "",
    pages: "",
    doi: "",
    pdf_url: "",
    status: "published",
    is_featured: false,
  });
  const [pdf, setPdf] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadIssues() {
      try {
        const data = await api("/api/admin/journal-issues");
        setIssues(Array.isArray(data) ? data : []);
      } catch {
        setIssues([]);
      }
    }
    loadIssues();
  }, []);

  useEffect(() => {
    if (!id) return;
    async function load() {
      try {
        const data = await api(`/api/admin/journal-articles/${id}`);
        setForm({
          issue_id: data.issue_id || "",
          title: data.title || "",
          authors: data.authors || "",
          abstract: data.abstract || "",
          keywords: data.keywords || "",
          pages: data.pages || "",
          doi: data.doi || "",
          pdf_url: data.pdf_url || "",
          status: data.status || "published",
          is_featured: Boolean(data.is_featured),
        });
      } catch (err) {
        setError(err.message);
      }
    }
    load();
  }, [id]);

  function updateField(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => data.append(key, key === "is_featured" ? (value ? "1" : "0") : value));
      if (pdf) data.append("pdf", pdf);
      await uploadApi(id ? `/api/admin/journal-articles/${id}` : "/api/admin/journal-articles", data, id ? "PUT" : "POST");
      navigate("/adxm/journal-articles");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout title={id ? "Edit Journal Article" : "Create Journal Article"} subtitle="Add article metadata, abstract, keywords, DOI and PDF file.">
      <form onSubmit={submit} className="mx-auto max-w-5xl rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        {error && <div className="mb-5 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}
        <div className="grid gap-5">
          <div>
            <label className="font-bold text-slate-700">Journal Issue</label>
            <select name="issue_id" value={form.issue_id} onChange={updateField} required className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3">
              <option value="">Select issue</option>
              {issues.map((issue) => <option key={issue.id} value={issue.id}>{issue.title}</option>)}
            </select>
          </div>
          <div>
            <label className="font-bold text-slate-700">Article Title</label>
            <input name="title" value={form.title} onChange={updateField} required className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>
          <div>
            <label className="font-bold text-slate-700">Authors</label>
            <input name="authors" value={form.authors} onChange={updateField} required placeholder="Author One, Author Two" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>
          <div>
            <label className="font-bold text-slate-700">Abstract</label>
            <textarea name="abstract" value={form.abstract} onChange={updateField} rows="7" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            <div>
              <label className="font-bold text-slate-700">Keywords</label>
              <input name="keywords" value={form.keywords} onChange={updateField} placeholder="AI, education, innovation" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
            <div>
              <label className="font-bold text-slate-700">Pages</label>
              <input name="pages" value={form.pages} onChange={updateField} placeholder="45-62" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
            <div>
              <label className="font-bold text-slate-700">DOI</label>
              <input name="doi" value={form.doi} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="font-bold text-slate-700">PDF Upload</label>
              <input type="file" accept="application/pdf" onChange={(e) => setPdf(e.target.files?.[0] || null)} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
            <div>
              <label className="font-bold text-slate-700">Existing/External PDF URL</label>
              <input name="pdf_url" value={form.pdf_url} onChange={updateField} placeholder="Optional external PDF URL" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="font-bold text-slate-700">Status</label>
              <select name="status" value={form.status} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <label className="mt-8 flex items-center gap-3 font-bold text-slate-700">
              <input type="checkbox" name="is_featured" checked={form.is_featured} onChange={updateField} /> Feature this article
            </label>
          </div>
          <button disabled={saving} className="rounded-xl bg-emerald-700 px-6 py-4 font-black text-white disabled:opacity-60">{saving ? "Saving..." : "Save Article"}</button>
        </div>
      </form>
    </AdminLayout>
  );
}
